import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useCheckout } from "../../context/CheckoutContext";
import { useNavigate } from "react-router-dom";
import CartItemList from "./CartItemList";
import CartSummaryBox from "./CartSummaryBox";
import EmptyCartMessage from "./EmptyCartMessage";
import UpsellRow from "../../components/Upsell/UpsellRow";
import api from "../../services/api";

const CartPage = () => {
  const {
    discountedCartItems,
    removeFromCart,
    updateCartItemQuantity,
    addToCart,
  } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { setCheckoutMode } = useCheckout();
  const navigate = useNavigate();

  const [edibleItems, setEdibleItems] = useState([]);
  const [accessoryItems, setAccessoryItems] = useState([]);

  const isEmpty = discountedCartItems.length === 0;

  const subtotal = discountedCartItems.reduce(
    (acc, item) => acc + item.unitPrice * item.quantity,
    0
  );

  const handleCheckout = (mode) => {
    setCheckoutMode(mode);
    navigate("/checkout");
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    document.title = "Cart";
  }, []);

  useEffect(() => {
    const fetchUpsells = async () => {
      try {
        const res = await api.get("/upsell-items");
        const all = res.data.filter((item) => item.isActive);

        const cartSubcategories = new Set(
          discountedCartItems
            .filter((item) => item.category !== "add-on")
            .map((item) => (item.subcategory || "").toLowerCase())
        );

        const filtered = all.filter(
          (upsell) =>
            !cartSubcategories.has((upsell.subcategory || "").toLowerCase())
        );

        setEdibleItems(filtered.filter((item) => item.type === "edible"));
        setAccessoryItems(filtered.filter((item) => item.type === "accessory"));
      } catch (err) {
        console.error("Error fetching upsell items", err);
      }
    };

    fetchUpsells();
  }, [discountedCartItems]);

  const buildCartItemFromUpsell = (upsell, customizationValues = {}) => {
    const customizations = (upsell.customizationFields || []).map((field) => ({
      key: field.key,
      label: field.label,
      value: customizationValues[field.key] || "",
    }));

    const quantity = upsell.quantity || 1;

    return {
      name: upsell.name,
      displayName: upsell.displayName || upsell.name,
      category: upsell.category || "add-on",
      type: upsell.type || "accessory",
      subcategory: upsell.subcategory || "",
      image: upsell.image,
      unitPrice: upsell.unitPrice,
      quantity,
      totalPrice: upsell.unitPrice * quantity,
      discountAmount: 0,
      discountType: null,
      size: null,
      flavor: null,
      color: null,
      itemsPerSet: 1,
      customizations,
      _id: upsell._id,
    };
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: 6 }}>
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 8 }}>
          Your Cart
        </Typography>

        {isEmpty ? (
          <EmptyCartMessage />
        ) : (
          <>
            <Box
              sx={{
                display: { xs: "block", md: "flex" },
                gap: 6,
                alignItems: "flex-start",
              }}>
              <Box sx={{ flex: 1 }}>
                <CartItemList
                  items={discountedCartItems}
                  onRemove={removeFromCart}
                  onUpdateQuantity={updateCartItemQuantity}
                />
              </Box>

              {!isMobile && (
                <Box sx={{ flex: "0 0 320px" }}>
                  <CartSummaryBox
                    subtotal={subtotal}
                    onGuestCheckout={() => handleCheckout("guest")}
                    onMemberCheckout={() => handleCheckout("member")}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 6 }}>
              <UpsellRow
                edibleItems={edibleItems}
                accessoryItems={accessoryItems}
                onAddToCart={async (upsellItem, customizationValues = {}) => {
                  const cartItem = buildCartItemFromUpsell(
                    upsellItem,
                    customizationValues
                  );
                  addToCart(cartItem);

                  if (user && isAuthenticated) {
                    try {
                      await api.post("/cart/add", cartItem);
                    } catch (err) {
                      console.error("Upsell cart sync failed:", err);
                    }
                  }
                }}
              />
            </Box>

            {isMobile && (
              <Box sx={{ mt: 5 }}>
                <CartSummaryBox
                  subtotal={subtotal}
                  onGuestCheckout={() => handleCheckout("guest")}
                  onMemberCheckout={() => handleCheckout("member")}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default CartPage;
